import React, { useState, useEffect } from "react"
import { useMutation } from "urql"

import { Input, Toggle } from "../../../../components/shared/Inputs"
import QuillEditor from "../../../../components/shared/QuillEditor"
import Typography from "../../../../components/shared/Typography"
import { useToast } from "../../../../contexts/ToastContext"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import ImagePickerV2 from "../shared/ImagePickerV2"

// ColorInput component
const ColorInput = ({ label, value, onChange, className = "" }) => (
  <div className={`space-y-1 ${className}`}>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <div className="flex gap-2">
      <input
        type="color"
        value={value || "#ffffff"}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-12 cursor-pointer rounded border border-gray-light"
      />
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="#ffffff" className="flex-1" />
    </div>
  </div>
)

const AboutEditor = ({ section, practice, onSave, onCancel }) => {
  const { handleUpdateSectionPreview, clearPreview } = useWebsiteBuilderV2()
  const [formData, setFormData] = useState({
    headline: section?.headline || "About Us",
    subheadline: section?.subheadline || "Learn more about our practice",
    body: section?.body || "",
    imageUrl: section?.imageUrl || practice?.aboutImageUrl || "",
    settings: {
      layout: "image_left", // image_left, image_right
      showCredentials: true,
      showTeam: false,
      showBackgroundColor: false,
      backgroundColor: "#ffffff",
      showCTA: true,
      ctaText: "Learn More",
      ctaAction: "page",
      ...(section?.settings || {}),
      ctaTarget: section?.settings?.ctaTarget || "about"
    }
  })

  const [errors, setErrors] = useState({})

  // GraphQL mutation for uploading images
  const UPLOAD_IMAGE_MUTATION = `
    mutation UploadImage($handle: String!, $description: String!, $provider: String) {
      uploadImage(handle: $handle, description: $description, provider: $provider) {
        result
        errors
        photoUrl
        altText
      }
    }
  `
  const [{ fetching: uploadFetching }, uploadImage] = useMutation(UPLOAD_IMAGE_MUTATION)
  const { showToast } = useToast()

  useEffect(() => {
    if (section) {
      setFormData({
        headline: section.headline || "About Us",
        subheadline: section.subheadline || "Learn more about our practice",
        body: section.body || "",
        imageUrl: section.imageUrl || practice?.aboutImageUrl || "",
        settings: {
          layout: "image_left",
          showCredentials: true,
          showTeam: false,
          showBackgroundColor: false,
          backgroundColor: "#ffffff",
          showCTA: true,
          ctaText: "Learn More",
          ctaAction: "page",
          ...section.settings,
          ctaTarget: section.settings?.ctaTarget || "about"
        }
      })
    }
  }, [section, practice])

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }

    // Update preview immediately for content changes
    if (section?.id && ["headline", "subheadline", "body", "imageUrl"].includes(field)) {
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData,
        [field]: value
      })
    }
  }

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }))

    // Update preview immediately for layout changes and other important settings
    if (
      section?.id &&
      ["layout", "showBackgroundColor", "backgroundColor", "showCTA", "ctaText", "ctaAction", "ctaTarget"].includes(key)
    ) {
      const updatedSettings = { ...formData.settings, [key]: value }
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData,
        settings: updatedSettings
      })
    }
  }

  // Handle image selection from ImagePickerV2
  const handleImageSelect = ({ handle, provider }) => {
    uploadImage({
      handle: handle,
      description: "about_section_image",
      provider: provider
    }).then((result) => {
      if (result?.data?.uploadImage?.result === "success") {
        handleChange("imageUrl", result.data.uploadImage.photoUrl)
        showToast("Image uploaded successfully!")
      } else {
        console.error(result) // eslint-disable-line no-console
        let errorMessage = "There was an error uploading your image. Please try again later or contact support."
        if (result.data?.uploadImage?.errors) errorMessage += ` ${result.data.uploadImage.errors}`
        showToast({
          type: "error",
          content: errorMessage
        })
      }
    })
  }

  // Handle image removal
  const handleImageRemove = () => {
    handleChange("imageUrl", "")
    showToast("Image removed.")
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.headline.trim()) {
      newErrors.headline = "Headline is required"
    }

    if (!formData.body.trim()) {
      newErrors.body = "About content is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (validateForm()) {
      onSave({
        layout: "about",
        ...formData
      })
    }
  }

  // Layout options with preview images
  const layoutOptions = [
    {
      id: "image_left",
      name: "Image Left",
      description: "Image on the left, text on the right",
      image: require("../../../../../assets/images/builder/v2/layout-images/img-l.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/img-l-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/img-l-selected.png")
    },
    {
      id: "image_right",
      name: "Image Right",
      description: "Image on the right, text on the left",
      image: require("../../../../../assets/images/builder/v2/layout-images/img-r.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/img-r-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/img-r-selected.png")
    }
  ]

  const LayoutOption = ({ option, isSelected, onSelect }) => {
    const [isHovered, setIsHovered] = useState(false)

    const getImageSource = () => {
      if (isSelected) {
        return option.imageSelected
      } else if (isHovered) {
        return option.imageHover
      } else {
        return option.image
      }
    }

    return (
      <div
        className="cursor-pointer transition-all"
        onClick={() => onSelect(option.id)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={option.name}>
        <img
          src={getImageSource()}
          alt={`${option.name} layout`}
          className="h-16 w-24 rounded-lg object-cover transition-all duration-200"
          onError={(e) => {
            console.warn(`Failed to load image: ${e.target.src}`) // eslint-disable-line no-console
            e.target.style.display = "none"
            e.target.nextSibling.style.display = "block"
          }}
        />
        {/* Fallback placeholder */}
        <div className="hidden h-16 w-24 items-center justify-center rounded-lg bg-gray-200">
          <div className="text-xs text-gray-500">{option.name}</div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Form Content */}
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pt-6">
        {/* Layout Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Layout</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Select a layout design for this section:</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 content-start items-start justify-start gap-3 text-clip p-0 [flex-flow:wrap]">
            {layoutOptions.map((option) => (
              <LayoutOption
                key={option.id}
                option={option}
                isSelected={formData.settings.layout === option.id}
                onSelect={(layoutId) => handleSettingsChange("layout", layoutId)}
              />
            ))}
          </div>
        </div>

        {/* Section Title Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Section title</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Add a short title to describe this section.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <input
                type="text"
                value={formData.headline}
                onChange={(e) => handleChange("headline", e.target.value)}
                placeholder="About Us"
                className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                required
              />
            </div>
            {errors.headline && (
              <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-red-500">
                <p className="block leading-[19.5px]">{errors.headline}</p>
              </div>
            )}
          </div>
        </div>

        {/* Description Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Description</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Add a detailed description to provide more context.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <QuillEditor
              value={formData.body}
              onChange={(value) => handleChange("body", value)}
              placeholder="Tell your story, your mission, and what makes you unique..."
              error={errors.body}
            />
            {errors.body && (
              <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-red-500">
                <p className="block leading-[19.5px]">{errors.body}</p>
              </div>
            )}
          </div>
        </div>

        {/* Call-to-action button Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Call-to-action button</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">
                Guide visitors to the next step with a clear call-to-action button.
              </p>
            </div>
          </div>

          {/* Show CTA Toggle */}
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
            <div className="flex items-center">
              <Toggle
                checked={formData.settings.showCTA !== undefined ? formData.settings.showCTA : true}
                onChange={(checked) => handleSettingsChange("showCTA", checked)}
              />
              <Typography variant="small" className="ml-3 font-medium text-gray-dark">
                Show call-to-action button
              </Typography>
            </div>
          </div>

          {/* CTA Configuration - Only show when CTA is enabled */}
          {(formData.settings.showCTA !== undefined ? formData.settings.showCTA : true) && (
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
              {/* CTA Type Selection */}
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-6 p-0">
                <div className="relative box-border flex shrink-0 content-center items-center justify-start gap-3 p-0 [flex-flow:wrap]">
                  <input
                    type="radio"
                    name="ctaAction"
                    value="page"
                    checked={formData.settings.ctaAction === "page"}
                    onChange={(e) => handleSettingsChange("ctaAction", e.target.value)}
                    className="relative size-6 shrink-0 accent-[#0d9da4]"
                  />
                  <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                    <p className="block whitespace-pre leading-[24px]">Page</p>
                  </div>
                </div>
                <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                  <input
                    type="radio"
                    name="ctaAction"
                    value="service"
                    checked={formData.settings.ctaAction === "service"}
                    onChange={(e) => handleSettingsChange("ctaAction", e.target.value)}
                    className="relative size-6 shrink-0 accent-[#0d9da4]"
                  />
                  <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[0] text-[#0b3954]">
                    <p className="block whitespace-pre leading-[24px]">Service</p>
                  </div>
                </div>
              </div>

              {/* Button Text */}
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
                <div className="relative box-border flex shrink-0 flex-row content-stretch items-start justify-start gap-1 p-0">
                  <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-[#0b3954]">
                    <p className="block whitespace-pre leading-[20px]">Button text</p>
                  </div>
                </div>
                <div className="relative w-full shrink-0 rounded-lg bg-white">
                  <input
                    type="text"
                    value={formData.settings.ctaText || "Learn More"}
                    onChange={(e) => handleSettingsChange("ctaText", e.target.value)}
                    placeholder="Learn More"
                    className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                  />
                </div>
              </div>

              {/* Target Selection */}
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
                <div className="relative box-border flex shrink-0 flex-row content-stretch items-start justify-start gap-1 p-0">
                  <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-[#0b3954]">
                    <p className="block whitespace-pre leading-[20px]">
                      {formData.settings.ctaAction === "page" ? "Page" : "Service"}
                    </p>
                  </div>
                </div>
                <div className="relative w-full shrink-0 rounded-lg bg-white">
                  <select
                    value={formData.settings.ctaTarget || ""}
                    onChange={(e) => handleSettingsChange("ctaTarget", e.target.value)}
                    className="relative box-border flex w-full appearance-none flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] bg-white px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none">
                    <option value="">Select</option>
                    {formData.settings.ctaAction === "page" ? (
                      <>
                        <option value="about">About</option>
                        <option value="services">Services</option>
                        <option value="contact">Contact</option>
                      </>
                    ) : (
                      <>
                        {practice?.services?.map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name}
                          </option>
                        ))}
                        {(!practice?.services || practice.services.length === 0) && (
                          <>
                            <option value="discovery-call">Discovery Call</option>
                            <option value="consultation">Consultation</option>
                          </>
                        )}
                      </>
                    )}
                  </select>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                    <svg
                      className="h-4 w-4 text-[#55778a] opacity-65"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {formData.settings.ctaAction === "service" && (
                  <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-[#55778a]">
                    <p className="block whitespace-pre leading-[19.5px]">
                      Go to your <span className="cursor-pointer text-[#0d9da4]">Services</span> to add additional
                      options.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Image Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Image</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Select images to feature in this section.</p>
            </div>
          </div>

          {/* Image Upload Area */}
          {formData.imageUrl ? (
            <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-center gap-4 p-0">
              <img src={formData.imageUrl} alt="About section image" className="h-16 w-16 rounded-lg object-cover" />
              <div className="relative box-border flex shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
                <ImagePickerV2
                  practice={practice}
                  onImageSelect={handleImageSelect}
                  fetching={uploadFetching}
                  showCurateTab={true}
                  currentImageUrl={formData.imageUrl}
                  trigger={
                    <button className="font-bold text-[#0d9da4] hover:underline" disabled={uploadFetching}>
                      Edit image
                    </button>
                  }
                />
                <button
                  className="font-bold text-red-500 hover:underline"
                  onClick={handleImageRemove}
                  disabled={uploadFetching}>
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <ImagePickerV2
              practice={practice}
              onImageSelect={handleImageSelect}
              fetching={uploadFetching}
              showCurateTab={true}
              currentImageUrl={formData.imageUrl}
              trigger={
                <div className="relative box-border flex w-full shrink-0 cursor-pointer flex-col content-stretch items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-[#c8d6df] bg-white p-6 transition-colors hover:border-[#0d9da4]">
                  <div className="relative flex size-10 shrink-0 items-center justify-center text-clip rounded-[100px] bg-[#e2f3f4]">
                    <svg className="h-5 w-5 text-[#0d9da4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                  <div className="relative shrink-0 text-nowrap text-center font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0d9da4]">
                    <p className="whitespace-pre leading-[24px]">
                      <span className="font-['Manrope:Bold',_sans-serif] font-bold">upload an image</span>
                    </p>
                  </div>
                  <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-[#55778a]">
                    <p className="block whitespace-pre leading-[19.5px]">Supported formats: PNG, JPG, SVG</p>
                  </div>
                </div>
              }
            />
          )}
        </div>

        {/* Section Background Color Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Section background color</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Include a background color in this section.</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center">
              <Toggle
                checked={formData.settings.showBackgroundColor || false}
                onChange={(checked) => handleSettingsChange("showBackgroundColor", checked)}
              />
              <Typography variant="small" className="ml-3 font-medium text-gray-dark">
                Background color
              </Typography>
            </div>

            {formData.settings.showBackgroundColor && (
              <ColorInput
                label="Background Color"
                value={formData.settings.backgroundColor}
                onChange={(value) => handleSettingsChange("backgroundColor", value)}
                className="mt-4"
              />
            )}
          </div>
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="box-border flex w-full flex-row content-stretch items-center justify-between border-t border-[#e4eff6] bg-white p-[32px]">
        <div className="relative box-border flex min-h-px min-w-px shrink-0 grow basis-0 flex-row content-stretch items-center justify-end gap-4 p-0">
          <button
            onClick={() => {
              clearPreview() // Clear any preview changes
              onCancel()
            }}
            className="relative box-border flex shrink-0 cursor-pointer flex-row content-stretch items-center justify-center gap-2 overflow-visible rounded border border-[#c8d6df] bg-white px-4 py-0 transition-colors hover:bg-gray-50">
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
              <p className="block whitespace-pre leading-[32px]">Close</p>
            </div>
          </button>
          <button
            onClick={handleSubmit}
            className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-center gap-2 rounded bg-[#0d9da4] px-4 py-0 transition-colors hover:bg-[#0b8a90]">
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-white">
              <p className="block whitespace-pre leading-[32px]">Save</p>
            </div>
          </button>
        </div>
      </div>
    </>
  )
}

export default AboutEditor
